import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { CheckCircle2, Download } from "lucide-react";

export default function Success() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const provider = sp.get("provider") || "stripe";
  const reference = sp.get("reference");
  const orderIdParam = sp.get("order_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const attemptsRef = useRef(0);
  const { clear } = useCart();

  useEffect(() => {
    let stop = false;

    const loadOrder = async (oid) => {
      try { const o = await api.get(`/orders/${oid}`); setOrder(o.data); } catch {}
    };

    const stripePoll = async () => {
      if (stop || !sessionId) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid"); clear();
          if (data.metadata?.order_id) await loadOrder(data.metadata.order_id);
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch {}
      attemptsRef.current += 1;
      if (attemptsRef.current >= 8) { setStatus("timeout"); return; }
      setTimeout(stripePoll, 2000);
    };

    const paystackVerify = async () => {
      try {
        const { data } = await api.get(`/checkout/paystack/verify/${reference}`);
        if (data.paid) { setStatus("paid"); clear(); if (data.order_id) await loadOrder(data.order_id); }
        else setStatus("expired");
      } catch { setStatus("error"); }
    };

    const razorpayCheck = async () => {
      // handled inline by verify endpoint before navigation, just fetch order
      if (orderIdParam) { setStatus("paid"); clear(); await loadOrder(orderIdParam); }
      else setStatus("error");
    };

    if (provider === "paystack" && reference) paystackVerify();
    else if (provider === "razorpay") razorpayCheck();
    else if (sessionId) stripePoll();
    else setStatus("error");

    return () => { stop = true; };
    // eslint-disable-next-line
  }, [sessionId, provider, reference, orderIdParam]);

  return (
    <div className="container-luxe py-32 text-center max-w-2xl">
      {status === "checking" && <><div className="animate-pulse text-6xl">•</div><h1 className="font-display text-3xl mt-6">Confirming payment…</h1></>}
      {status === "paid" && (
        <>
          <CheckCircle2 className="text-[#00A86B] mx-auto" size={64} strokeWidth={1.5} />
          <h1 className="font-display text-4xl mt-6">Payment Confirmed</h1>
          <p className="text-gray-400 mt-4">Thank you for your purchase. Your downloads are unlocked.</p>
          {order && (
            <div className="glass rounded-2xl p-8 mt-10 text-left">
              <div className="text-xs tracking-widest uppercase text-gray-500 mb-3">Order #{order.id.slice(0, 8)} · {order.provider || "stripe"}</div>
              {order.items?.map((it) => (
                <div key={it.product_id} className="flex justify-between items-center py-3 border-b border-white/5">
                  <span>{it.name}</span>
                  <Link data-testid={`download-${it.product_id}`} to="/dashboard?tab=downloads" className="text-[#D4AF37] text-sm flex items-center gap-2"><Download size={14} /> Download</Link>
                </div>
              ))}
            </div>
          )}
          <Link to="/dashboard?tab=downloads" className="btn-gold mt-10">Go to Dashboard</Link>
        </>
      )}
      {(status === "expired" || status === "timeout" || status === "error") && (
        <>
          <h1 className="font-display text-3xl">{status === "expired" ? "Session expired" : "Something went wrong"}</h1>
          <Link to="/cart" className="btn-gold mt-8">Back to Cart</Link>
        </>
      )}
    </div>
  );
}
